/**
 * Permission Audit Configuration
 *
 * This file serves as the single source of truth for all permission points in the system.
 * It is used by the Role Permission Editor to display and manage permissions.
 */

import { UserRole } from '../types/user.types';
import { StoragePermissions, StoragePathPermission } from '../types/permission.types';
import { ACTIVE_ROLES } from '../utils/roleUtils';

// ============================================================
// Dashboard Cards
// ============================================================

export interface DashboardCardDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'main' | 'admin';
  defaultEnabledRoles: UserRole[];
}

export const DASHBOARD_CARDS: DashboardCardDefinition[] = [
  // Main Modules
  {
    id: 'hq',
    name: 'HQ',
    description: 'HQ development management and sales tools',
    icon: 'Building2',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.HQ_ADVERTISING,
    ],
  },
  {
    id: 'crm',
    name: 'CRM',
    description: 'Contact and lead management',
    icon: 'Users',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'feedback',
    name: 'Report an Issue',
    description: 'Submit a bug, missing-feature request, or UX concern to the team',
    icon: 'MessageSquare',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.PROPCLOUDAI_USER_MANAGEMENT,
      UserRole.PROPCLOUDAI_CRM,
      UserRole.PROPCLOUDAI_ADVERTISING,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_REFERRAL_MANDATE,
      UserRole.HQ_CALL_CENTRE_AGENT,
      UserRole.HQ_VIEWING_COORDINATOR,
      UserRole.HQ_ADVERTISING,
      UserRole.INDEPENDENT_PARTNER_LVL3,
      UserRole.PARTNER_AGENT_LVL3,
      UserRole.VIEWING_AGENT,
    ],
  },
  {
    id: 'advertising',
    name: 'Advertising',
    description: 'Marketing and advertising campaigns',
    icon: 'Megaphone',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_ADVERTISING,
    ],
  },
  {
    id: 'leadStats',
    name: 'Lead Stats',
    description: 'Lead generation analytics and reports',
    icon: 'BarChart',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_ADVERTISING,
    ],
  },
  {
    id: 'viewingManagement',
    name: 'Viewing Management',
    description: 'Manage property viewings and agents',
    icon: 'Eye',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_VIEWING_AGENT,
    ],
  },
  {
    id: 'viewingOversight',
    name: 'Viewing Oversight',
    description: 'Complete oversight of all property viewings',
    icon: 'Eye',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_VIEWING_COORDINATOR,
    ],
  },
  {
    id: 'consultations',
    name: 'Consultations',
    description: 'Consultation scheduling and management',
    icon: 'Calendar',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'training',
    name: 'Training',
    description: 'Training materials and resources',
    icon: 'GraduationCap',
    category: 'main',
    defaultEnabledRoles: ACTIVE_ROLES, // All active roles have training access
  },
  {
    id: 'my_documents',
    name: 'My Documents',
    description: 'View and manage signed documents',
    icon: 'FileText',
    category: 'main',
    defaultEnabledRoles: ACTIVE_ROLES, // All active roles have document access
  },
  {
    id: 'accounting',
    name: 'Invoices',
    description: 'Manage invoices, pro-formas, and payments',
    icon: 'Calculator',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'old_deals',
    name: 'Old Deals',
    description: 'Import and manage historical deal records',
    icon: 'Archive',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'partner',
    name: 'Partner',
    description: 'Partner portal and collaboration (deprecated)',
    icon: 'Users',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'hq_management',
    name: 'HQ Management',
    description: 'HQ organizational setup, agents, leads, and marketing',
    icon: 'Building2',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },

  // Admin Tools
  {
    id: 'credit_scores',
    name: 'Credit Scores',
    description: 'View credit checks and pre-qualifications',
    icon: 'CreditCard',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'credit_score_view',
    name: 'Credit Score View',
    description: 'View credit score categories for assigned contacts',
    icon: 'CreditCard',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'all_contacts',
    name: 'All Contacts',
    description: 'The full contact database — search, filter and export every contact by source, consultant, date and more',
    icon: 'UsersRound',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'buyers_list',
    name: 'Buyers List',
    description: 'View all buyers who purchased a development, with demographic insights and export',
    icon: 'Users',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'custom_audiences',
    name: 'Custom Audiences',
    description: 'View and edit saved audiences used to target WhatsApp and marketing campaigns',
    icon: 'Filter',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'user_management',
    name: 'User Management',
    description: 'Manage users and their settings',
    icon: 'UserCog',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'roles_permissions',
    name: 'Roles & Permissions',
    description: 'Configure role permissions',
    icon: 'Shield',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'secrets_management',
    name: 'Secrets Management',
    description: 'Rotate API keys / server-side secrets (Google Secret Manager)',
    icon: 'Key',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'heidi_console',
    name: 'Heidi Console',
    description: 'Bonds AI assistant (secret until launch): settings and the required-documents list',
    icon: 'UserCheck',
    category: 'main',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'wendy_console',
    name: 'Wendy Console',
    description: 'AI call-centre agent (invisible until launch): settings, test chat, conversations',
    icon: 'Headset',
    category: 'main',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'voice_lab',
    name: 'Cindy Voice Lab',
    description: 'Test bench: talk to Cindy by voice and compare engines. Invisible to leads',
    icon: 'Phone',
    category: 'main',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'cindy_test_form',
    name: 'Cindy Test Form',
    description: 'Submit yourself as a lead and watch the full Cindy workflow run',
    icon: 'Bot',
    category: 'main',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'notifications_admin',
    name: 'Notifications (Super Admin)',
    description: 'Every in-app notification the system has sent, across all users',
    icon: 'Bell',
    category: 'main',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'management_scoreboard',
    name: 'Management Scoreboard',
    description: 'Deal strip, all boards incl. Money, AI summaries and Intent Review (super admin only)',
    icon: 'BarChart3',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'lead_assignment_history',
    name: 'Lead Assignment History',
    description: 'Every lead owner change incl. Capacity Cover moves (super admin only)',
    icon: 'History',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'lead_distribution',
    name: 'Lead Distribution',
    description: 'Performance-weighted lead sharing by tier (super admin only)',
    icon: 'Scale',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'aria_mission_control',
    name: 'Cindy Mission Control',
    description: 'Live view of the AI consultant (funnel, replay; controls stay super-admin)',
    icon: 'Bot',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
  },
  {
    id: 'cindy_leads',
    name: 'Cindy Leads',
    description: 'New-lead takeover, old-lead warm-up, capacity and safety for the AI assistant',
    icon: 'Zap',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'careers_applications',
    name: 'Careers',
    description: 'Website careers applications, AI qualification rules and manual verdicts',
    icon: 'Briefcase',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'cindy_analytics',
    name: 'Cindy Analytics',
    description: 'Cindy performance stats — milestones split New Leads vs Old Lead Warm-up',
    icon: 'BarChart3',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
  },
  {
    id: 'cindy_cost_monitor',
    name: 'Cindy Cost Monitor',
    description: 'LLM spend, cache performance, caps, savings vs baseline and projections',
    icon: 'DollarSign',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER, UserRole.HQ_MANAGER],
  },
  {
    id: 'aria_training_console',
    name: 'Cindy Training Console',
    description: 'Curate the AI consultant (knowledge, documents, persona, settings)',
    icon: 'Sparkles',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'user_impersonation',
    name: 'User Impersonation',
    description: 'Test as another user (for debugging)',
    icon: 'UserSwitch',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'graphicDesign',
    name: 'Graphic Design',
    description: 'Upload development marketing assets',
    icon: 'Palette',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_ADVERTISING,
    ],
  },
  {
    id: 'partner_settings',
    name: 'Partner Settings',
    description: 'Partner integrations (SendGrid, Meta, SMS)',
    icon: 'Settings2',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'system_settings',
    name: 'System Settings',
    description: 'Advanced system configuration',
    icon: 'Settings',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'general_settings',
    name: 'General Settings',
    description: 'Configure commissions, VAT, and general settings',
    icon: 'Settings',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'activities',
    name: 'Activities',
    description: 'View HQ and Partner activity logs',
    icon: 'Activity',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'reports',
    name: 'Reports',
    description: 'View and export system reports',
    icon: 'FileText',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'statistics',
    name: 'Statistics',
    description: 'View system statistics and analytics',
    icon: 'TrendingUp',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'system_cleanup',
    name: 'System Cleanup',
    description: 'Clean up system data and orphaned records',
    icon: 'AlertOctagon',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'my_cindy_orbit',
    name: 'My Cindy Orbit',
    description: 'Live orbit of the consultant\'s own Cindy-managed leads (contact owner only)',
    icon: 'Orbit',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'consultation_insights',
    name: 'Consultation Insights',
    description: 'Weekly meetings, outcomes, notes and AI bottlenecks for every consultant (everyone)',
    icon: 'Lightbulb',
    category: 'main',
    defaultEnabledRoles: Object.values(UserRole),
  },
  {
    id: 'ticket_management',
    name: 'Support Hub',
    description: 'Unified queue for tickets, feedback and screen-review notes (support team — super users only)',
    icon: 'Ticket',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'ai_assistant',
    name: 'AI Assistant',
    description: 'Configure AI assistant settings and knowledge base',
    icon: 'Brain',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'notification_settings',
    name: 'Notification Settings',
    description: 'Configure development transfer date notifications',
    icon: 'Bell',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'viewing_settings',
    name: 'Viewing Settings',
    description: 'Configure viewing agent webhooks and defaults',
    icon: 'Eye',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'viewing_webhook_testing',
    name: 'Viewing Webhook Testing',
    description: 'Test viewing webhooks for GoHighLevel integration',
    icon: 'Webhook',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'public_website_manager',
    name: 'Public Website Manager',
    description: 'Manage which properties appear on the Invest SA public website',
    icon: 'Globe',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'ai_training',
    name: 'AI Training',
    description: 'Configure AI email generation prompts, terminology, and training',
    icon: 'Sparkles',
    category: 'admin',
    defaultEnabledRoles: [UserRole.PROPCLOUDAI_SUPER_USER],
  },
  {
    id: 'lead_automation',
    name: 'Lead Automation',
    description: 'Configure automated lead follow-up pipeline and notifications',
    icon: 'Zap',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'call_centre_config',
    name: 'Call Centre Config',
    description: 'Configure auto-push to Euphoria dialer for incoming leads',
    icon: 'Phone',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'dialer_simulation',
    name: 'Dialer Test',
    description: 'Push simulated leads to the Euphoria dialer for testing',
    icon: 'PhoneCall',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'syndication_config',
    name: 'Syndication',
    description: 'Configure PropData syndication to property portals',
    icon: 'Share2',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'whatsapp_followups_config',
    name: 'WhatsApp Follow-Ups Config',
    description: 'Configure scheduled WhatsApp follow-up templates and the public review URL',
    icon: 'MessageSquare',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'whatsapp_bot_builder',
    name: 'WhatsApp Bot Builder',
    description: 'Visual drag-and-drop builder for automated WhatsApp reply flows and menus',
    icon: 'Workflow',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'whatsapp_automation_center',
    name: 'WhatsApp Automation Center',
    description: 'Control panel for all automated outbound WhatsApp messages (reservation/OTP, lead follow-up, consultation reminders)',
    icon: 'Zap',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'whatsapp_bot_analytics',
    name: 'WhatsApp Bot Analytics',
    description: 'Conversation volume, flow performance, drop-off funnels and attribution for the published WhatsApp bot',
    icon: 'BarChart3',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'reviews_admin',
    name: 'Customer Reviews',
    description: 'View customer reviews and per-consultant rating insights',
    icon: 'Star',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'website_cms',
    name: 'Website CMS',
    description: 'Manage public website content, videos, and settings',
    icon: 'Globe',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'mobile_app_cms',
    name: 'Mobile App CMS',
    description: 'Manage mobile app content, users, and push notifications',
    icon: 'Smartphone',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'media_manager',
    name: 'Media Manager',
    description: 'Upload and manage development photos and videos for marketing',
    icon: 'Images',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'call_centre_calendar_book',
    name: 'Calendar Booking',
    description: 'Book meetings and calls on consultant calendars on behalf of contacts',
    icon: 'CalendarPlus',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'leadIntelligence',
    name: 'Lead Intelligence',
    description: 'Marketing ROI, source performance & conversion funnel analytics',
    icon: 'BarChart3',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'activeSalesPipeline',
    name: 'Active Sales Pipeline',
    description: 'Every in-progress deal capture with contact, development, consultant, source & CC-qualified agent',
    icon: 'Activity',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'vip_clients',
    name: 'VIP Clients',
    description: 'Complete sales, CC qualified, mobile app, website & WhatsApp leads',
    icon: 'Trophy',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'consultant_quiz_results',
    name: 'Training Management',
    description: 'Upload training videos, track consultant progress and quiz results',
    icon: 'Monitor',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'bonus_audit',
    name: 'Bonus Audit',
    description: 'Monitor and verify consultant monthly & quarterly bonus qualifications',
    icon: 'Award',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'call_centre_bonus_audit',
    name: 'Call Centre Bonus Audit',
    description: 'First-contact audit per 100% complete: call centre vs consultant, with evidence',
    icon: 'Headset',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'deposit_waiver_report',
    name: 'Deposit Waiver Report',
    description: 'All deals with deposit waivers or money at an attorney — excluded from sales stats',
    icon: 'Scale',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'bonds_bonus_dashboard',
    name: 'Bonds Bonus Dashboard',
    description: 'Personal bond bonus view — only the bonds consultant sees their own tracking',
    icon: 'TrendingUp',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.HQ_BONDS,
    ],
  },
  {
    id: 'outstanding_bonds',
    name: 'Outstanding Bonds',
    description: 'Dashboard of PropInvest Bonds deals outstanding longer than 2 weeks (HQ management)',
    icon: 'Landmark',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'bonds_bonus_admin',
    name: 'Bonds Bonus Admin',
    description: 'Audit bond bonuses, apply overrides, and mark bonuses as paid',
    icon: 'ShieldCheck',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'consultant_transfer',
    name: 'Consultant Transfer',
    description: 'Transfer contacts / leads to another consultant when one leaves',
    icon: 'UserMinus',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'lead_assignment_audit',
    name: 'Lead Assignment Audit',
    description: 'Per-contact ownership history — investigate disputes and trace process-driven reassignments',
    icon: 'Activity',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'consultant_reviews',
    name: 'Consultant Reviews',
    description: 'Monthly lead-quality + ethics reviews for LVL1/LVL2 consultants. Manage suspensions and audit conflicts.',
    icon: 'ClipboardCheck',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'my_monthly_review',
    name: 'My Monthly Review',
    description: 'Complete your monthly lead-quality & ethics review',
    icon: 'ClipboardCheck',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'escalations',
    name: 'Escalations',
    description: 'Leads the AI assistant handed to a consultant',
    icon: 'AlertTriangle',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'booking_outcomes',
    name: 'Booking Outcomes',
    description: 'Record what happened with each consultation (compulsory)',
    icon: 'ClipboardCheck',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'expired_reservations',
    name: 'Expired Reservations',
    description: 'Restore expired reservations from their 14-day snapshots',
    icon: 'History',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
      UserRole.HQ_MANAGER,
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'performance_hub',
    name: 'Performance Hub',
    description: 'Your deal pipeline, commissions, payments and loans at a glance',
    icon: 'Gauge',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'disposition_reconciliation',
    name: 'Disposition Reconciliation',
    description: 'Audit and reconcile Euphoria dispositions with callDispositionLogs to protect agent incentives',
    icon: 'Activity',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'consultant_lead_report',
    name: 'Consultant Lead Report',
    description: 'Per-consultant engagement events (initial enquiries + WhatsApp/SMS/email replies) over a date range, exportable to Excel',
    icon: 'ClipboardList',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'call_centre_stats',
    name: 'Call Centre Stats',
    description: 'Personal call statistics, disposition breakdown, and performance metrics',
    icon: 'Headset',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CALL_CENTRE_AGENT,
      UserRole.PROPCLOUDAI_SUPER_USER,
    ],
  },
  {
    id: 'call_centre_booking',
    name: 'Book Consultation',
    description: 'Book meetings on consultant calendars on behalf of contacts',
    icon: 'CalendarPlus',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.HQ_CALL_CENTRE_AGENT,
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
  {
    id: 'call_centre_overview',
    name: 'Call Centre Overview',
    description: 'All agent performance stats, leaderboard, and disposition breakdown',
    icon: 'BarChart3',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
    ],
  },
  {
    id: 'investmentProposals',
    name: 'Investment Proposals',
    description: 'Build multi-property investment projections for clients (20-yr cash flow, IRR, Sec 13sex, branded PDF)',
    icon: 'FileText',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'capital_investment',
    name: 'Capital Investment',
    description: 'Capital investment leads + insights — only the capital consultant (Pieter Piek) and super users, whatever the role setting',
    icon: 'Landmark',
    category: 'main',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    id: 'allProposals',
    name: 'All Investment Proposals',
    description: 'Company-wide view of every proposal generated by every consultant',
    icon: 'FileText',
    category: 'admin',
    defaultEnabledRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
    ],
  },
];

// ============================================================
// Menu Groups and Items
// ============================================================

export interface MenuItemDefinition {
  id: string;
  name: string;
  description: string;
  action?: string;
  path?: string;
  requiresPasswordProtection?: boolean;
}

export interface MenuGroupDefinition {
  id: string;
  name: string;
  description: string;
  cardId: string;
  icon: string;
  items: MenuItemDefinition[];
}

export const HQ_MENU_GROUPS: MenuGroupDefinition[] = [
  {
    id: 'hq.development',
    name: 'Development',
    description: 'Development management tools',
    cardId: 'hq',
    icon: 'Building2',
    items: [
      { id: 'hq.development.search', name: 'Search Developments', description: 'Search and view developments' },
      { id: 'hq.development.create', name: 'Create Development', description: 'Create new development' },
      { id: 'hq.development.edit', name: 'Edit Development', description: 'Edit existing development' },
      { id: 'hq.development.editUnits', name: 'Edit Units', description: 'Manage units in developments' },
      { id: 'hq.development.viewIncome', name: 'View Income', description: 'View potential income' },
      { id: 'hq.development.allIncome', name: 'All Income', description: 'Income across all developments' },
      { id: 'hq.development.delete', name: 'Delete Development', description: 'Delete development (Super User)', requiresPasswordProtection: true },
    ],
  },
  {
    id: 'hq.payments',
    name: 'Payments & Invoicing',
    description: 'Payment and invoice management',
    cardId: 'hq',
    icon: 'Wallet',
    items: [
      { id: 'hq.payments.proforma', name: 'Generate Pro-Forma', description: 'Create pro-forma invoices' },
      { id: 'hq.payments.invoice', name: 'Generate Invoice', description: 'Convert pro-formas to invoices' },
      { id: 'hq.payments.payAgent', name: 'Pay HQ Agent', description: 'Process agent payments' },
      { id: 'hq.payments.payPartner', name: 'Pay Partner', description: 'Process partner payments' },
      { id: 'hq.payments.manageReservationAmounts', name: 'Reservation Amounts', description: 'Manage reservation payments', requiresPasswordProtection: true },
      { id: 'hq.payments.consultantLoans', name: 'Consultant Loans', description: 'Manage consultant loans', requiresPasswordProtection: true },
    ],
  },
  {
    id: 'hq.salesTools',
    name: 'Sales Tools',
    description: 'Sales and reservation tools',
    cardId: 'hq',
    icon: 'ClipboardList',
    items: [
      { id: 'hq.salesTools.preReserve', name: 'Pre-Reserve Unit', description: 'Create pre-reservations' },
      { id: 'hq.salesTools.bulkPreReserve', name: 'Bulk Pre-Reserve', description: 'Pre-reserve multiple units for one contact' },
      { id: 'hq.salesTools.reserve', name: 'Reserve Unit', description: 'Create reservations' },
      { id: 'hq.salesTools.captureDeal', name: 'Capture Deal', description: 'Capture new deals' },
      { id: 'hq.salesTools.cancelDeal', name: 'Cancel Deal', description: 'Cancel existing deals', requiresPasswordProtection: true },
      { id: 'hq.salesTools.bookViewing', name: 'Book Viewing', description: 'Book property viewings' },
      { id: 'hq.salesTools.callCentreBook', name: 'Book on Consultant Calendar', description: 'Book a meeting or call on behalf of a contact', path: '/admin/call-centre-calendar-booking' },
      { id: 'hq.salesTools.manageReservations', name: 'Manage Reservations', description: 'Review reservation docs', requiresPasswordProtection: true },
      { id: 'hq.salesTools.manageClientResponses', name: 'Client Responses', description: 'Review client acceptances', requiresPasswordProtection: true },
      { id: 'hq.salesTools.manageOTP', name: 'Manage OTP', description: 'Review OTP documents', requiresPasswordProtection: true },
    ],
  },
  {
    id: 'hq.reports',
    name: 'Reports',
    description: 'Various reports and analytics',
    cardId: 'hq',
    icon: 'BarChart3',
    items: [
      { id: 'hq.reports.sales', name: 'Sales Reports', description: 'Sales performance reports' },
      { id: 'hq.reports.commissions', name: 'Commissions Report', description: 'Commission calculations' },
      { id: 'hq.reports.bonds', name: 'Bonds Report', description: 'Bond application reports' },
      { id: 'hq.reports.bondsCommission', name: 'Bonds Commission', description: 'Bond commission reports' },
      { id: 'hq.reports.cash', name: 'Cash Deals', description: 'Cash transaction reports' },
      { id: 'hq.reports.cancelled', name: 'Cancelled Deals', description: 'Cancelled deals analysis' },
      { id: 'hq.reports.mandates', name: 'Mandates Report', description: 'Mandate performance' },
      { id: 'hq.reports.loans', name: 'Loan Report', description: 'Consultant loan reports' },
      { id: 'hq.reports.salesCommission', name: 'Sales Commission', description: 'Sales commission reports' },
      { id: 'hq.reports.leads', name: 'Lead Report', description: 'Lead generation reports' },
      { id: 'hq.reports.myAllocatedUnits', name: 'My Allocated Units', description: 'Units allocated to current user' },
    ],
  },
  {
    id: 'hq.statistics',
    name: 'Statistics',
    description: 'Performance statistics',
    cardId: 'hq',
    icon: 'TrendingUp',
    items: [
      { id: 'hq.statistics.statsHistoryHQ', name: 'Stats HQ', description: 'Monthly sales stats' },
      { id: 'hq.statistics.statsHistoryIndependent', name: 'Stats Independent', description: 'Independent consultant stats' },
      { id: 'hq.statistics.statsHistoryPartner', name: 'Stats Partner', description: 'Partner performance stats' },
      { id: 'hq.statistics.statsHistoryPartnerAgent', name: 'Stats Partner Agent', description: 'Partner agent stats' },
    ],
  },
  {
    id: 'hq.templates',
    name: 'Templates',
    description: 'Document templates',
    cardId: 'hq',
    icon: 'FileCode',
    items: [
      { id: 'hq.templates.mandate', name: 'Mandate', description: 'Mandate document template' },
      { id: 'hq.templates.reservation', name: 'Reservation', description: 'Reservation template' },
      { id: 'hq.templates.consultantContract', name: 'Consultant Contract', description: 'Consultant employment' },
      { id: 'hq.templates.independentConsultantContract', name: 'Independent Contract', description: 'Independent consultant' },
      { id: 'hq.templates.adminContract', name: 'Admin Contract', description: 'Admin employment' },
      { id: 'hq.templates.independentPartnerContract', name: 'Partner Contract', description: 'Independent partner' },
    ],
  },
  {
    id: 'hq.developmentDocs',
    name: 'Development Docs',
    description: 'Development documentation',
    cardId: 'hq',
    icon: 'FileSearch',
    items: [
      { id: 'hq.developmentDocs.viewDocs', name: 'View Docs', description: 'View development docs' },
    ],
  },
  {
    id: 'hq.leads',
    name: 'Lead Management',
    description: 'Lead capture, import, and assignment tools',
    cardId: 'hq',
    icon: 'TrendingUp',
    items: [
      { id: 'hq.leads.manualCapture', name: 'Manual Lead Capture', description: 'Manually capture new leads', path: '/admin/capture-lead' },
      { id: 'hq.leads.import', name: 'Import Contacts', description: 'Bulk import contacts from CSV', path: '/admin/import-contacts' },
      { id: 'hq.leads.assign', name: 'Assign Contacts', description: 'Assign contacts to agents', path: '/admin/assign-contacts' },
    ],
  },
];

export const CRM_MENU_GROUPS: MenuGroupDefinition[] = [
  {
    id: 'crm.contacts',
    name: 'Contacts',
    description: 'Contact management',
    cardId: 'crm',
    icon: 'Users',
    items: [
      { id: 'crm.contacts.view', name: 'View Contacts', description: 'View all contacts' },
      { id: 'crm.contacts.add', name: 'Add Contact', description: 'Add new contact' },
      { id: 'crm.contacts.import', name: 'Import Contacts', description: 'Import from file' },
    ],
  },
  {
    id: 'crm.leads',
    name: 'Leads',
    description: 'Lead management',
    cardId: 'crm',
    icon: 'UserCheck',
    items: [
      { id: 'crm.leads.view', name: 'View Leads', description: 'View all leads' },
      { id: 'crm.leads.assign', name: 'Assign Leads', description: 'Assign to agents' },
      { id: 'crm.leads.convert', name: 'Convert Leads', description: 'Convert to deals' },
    ],
  },
  {
    id: 'crm.activities',
    name: 'Activities',
    description: 'Activity tracking',
    cardId: 'crm',
    icon: 'Activity',
    items: [
      { id: 'crm.activities.view', name: 'View Activities', description: 'View all activities' },
      { id: 'crm.activities.schedule', name: 'Schedule Activity', description: 'Schedule new activity' },
    ],
  },
];

export const ADVERTISING_MENU_GROUPS: MenuGroupDefinition[] = [
  {
    id: 'advertising.campaigns',
    name: 'Campaigns',
    description: 'Marketing campaign management',
    cardId: 'advertising',
    icon: 'Megaphone',
    items: [
      { id: 'advertising.campaigns.create', name: 'Create Campaign', description: 'Create new marketing campaign', path: '/advertising/create-campaign' },
      { id: 'advertising.campaigns.view', name: 'View Campaigns', description: 'View all active campaigns', path: '/advertising/campaigns' },
      { id: 'advertising.campaigns.analytics', name: 'Campaign Analytics', description: 'View campaign performance', path: '/advertising/analytics' },
    ],
  },
  {
    id: 'advertising.leads',
    name: 'Lead Management',
    description: 'Lead capture and management tools',
    cardId: 'advertising',
    icon: 'Users',
    items: [
      { id: 'advertising.leads.manualCapture', name: 'Manual Lead Capture', description: 'Manually capture new leads', path: '/admin/capture-lead' },
      { id: 'advertising.leads.import', name: 'Import Contacts', description: 'Bulk import contacts from CSV', path: '/admin/import-contacts' },
      { id: 'advertising.leads.assign', name: 'Assign Contacts', description: 'Assign contacts to agents', path: '/admin/assign-contacts' },
    ],
  },
  {
    id: 'advertising.tools',
    name: 'Marketing Tools',
    description: 'Marketing automation and tools',
    cardId: 'advertising',
    icon: 'Wrench',
    items: [
      { id: 'advertising.tools.utm', name: 'UTM Builder', description: 'Create UTM tracking codes', path: '/admin/utm-builder' },
      { id: 'advertising.tools.formBuilder', name: 'Lead Form Builder', description: 'Create custom lead capture forms', path: '/admin/lead-form-builder' },
      { id: 'advertising.tools.formList', name: 'Form List', description: 'Manage existing forms', path: '/admin/lead-forms' },
      { id: 'advertising.tools.emailTemplates', name: 'Email Templates', description: 'Create and manage email templates', path: '/advertising/email-templates' },
    ],
  },
];

// ============================================================
// Storage Permissions
// ============================================================

export interface StoragePermissionDefinition {
  path: string;
  name: string;
  description: string;
  readRoles: UserRole[];
  writeRoles: UserRole[];
  note?: string;
}

export const STORAGE_PERMISSIONS: StoragePermissionDefinition[] = [
  {
    path: '/developments/**',
    name: 'Development Documents',
    description: 'Development-related files and images',
    readRoles: ACTIVE_ROLES,
    writeRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    path: '/reservations/**',
    name: 'Reservation Documents',
    description: 'Reservation and FICA documents',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Auth-only rule for reliability
    note: 'Uses auth-only rule due to dual-ID issues',
  },
  {
    path: '/deals/**',
    name: 'Deal Documents',
    description: 'OTP, co-signed docs, etc.',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Auth-only rule for reliability
    note: 'Uses auth-only rule due to dual-ID issues',
  },
  {
    path: '/documents/**',
    name: 'General Documents',
    description: 'General document storage',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Auth-only rule for reliability
    note: 'Uses auth-only rule due to dual-ID issues',
  },
  {
    path: '/invoices/**',
    name: 'Invoice Documents',
    description: 'Pro-forma and final invoices',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Auth-only rule for reliability
    note: 'Uses auth-only rule due to dual-ID issues',
  },
  {
    path: '/consultantLoans/**',
    name: 'Consultant Loan Documents',
    description: 'Loan disbursement, payment proofs',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Auth-only rule for reliability
    note: 'Uses auth-only rule due to dual-ID issues',
  },
  {
    path: '/emailTemplates/**',
    name: 'Email Templates',
    description: 'Email template images and assets',
    readRoles: ACTIVE_ROLES,
    writeRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_ADVERTISING,
    ],
  },
  {
    path: '/marketingAssets/**',
    name: 'Marketing Assets',
    description: 'AI-generated social posts, ads',
    readRoles: ACTIVE_ROLES,
    writeRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_ADVERTISING,
      UserRole.HQ_CONSULTANT_LVL1,
      UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    ],
  },
  {
    path: '/templates/**',
    name: 'Document Templates',
    description: 'Mandate, reservation templates',
    readRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
    ],
    writeRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_BONDS,
    ],
  },
  {
    path: '/viewings/**',
    name: 'Viewing Documents',
    description: 'Viewing-related files',
    readRoles: ACTIVE_ROLES,
    writeRoles: [
      UserRole.PROPCLOUDAI_SUPER_USER,
      UserRole.HQ_MANAGER,
      UserRole.HQ_ADMIN,
      UserRole.HQ_VIEWING_AGENT,
      UserRole.HQ_VIEWING_COORDINATOR,
    ],
  },
  {
    path: '/users/{userId}/**',
    name: 'User Documents',
    description: 'Profile photos and personal docs',
    readRoles: ACTIVE_ROLES,
    writeRoles: ACTIVE_ROLES, // Owner or admin
    note: 'Owner can write own files, admins can write any',
  },
];

// ============================================================
// Data Access Levels
// ============================================================

export interface DataAccessDefinition {
  id: string;
  name: string;
  description: string;
}

export const DATA_ACCESS_LEVELS: DataAccessDefinition[] = [
  {
    id: 'own',
    name: 'Own Data Only',
    description: 'User can only view their own records (contacts, deals, leads)',
  },
  {
    id: 'team',
    name: 'Team Data',
    description: 'User can view records from their team/department',
  },
  {
    id: 'partner',
    name: 'Partner Data',
    description: 'User can view all records within their partner organization',
  },
  {
    id: 'all',
    name: 'All Data',
    description: 'User can view all records across the entire system',
  },
];

// ============================================================
// Default Storage Permissions by Role
// ============================================================

// Helper to create full read/write permission
const fullAccess: StoragePathPermission = { read: true, write: true };
// Helper to create read-only permission
const readOnly: StoragePathPermission = { read: true, write: false };
// Helper to create no access permission
const noAccess: StoragePathPermission = { read: false, write: false };

/**
 * Default storage permissions for each active role.
 * These are used as fallbacks when no Firestore configuration exists.
 */
export const DEFAULT_STORAGE_PERMISSIONS: Record<string, StoragePermissions> = {
  // Super User - Full access to everything
  [UserRole.PROPCLOUDAI_SUPER_USER]: {
    developments: fullAccess,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: fullAccess,
    documents: fullAccess,
    invoices: fullAccess,
    marketingAssets: fullAccess,
    emailTemplates: fullAccess,
    userPhotos: fullAccess,
    viewings: fullAccess,
    templates: fullAccess,
  },

  // HQ Manager - Full access to most paths
  [UserRole.HQ_MANAGER]: {
    developments: fullAccess,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: fullAccess,
    documents: fullAccess,
    invoices: fullAccess,
    marketingAssets: fullAccess,
    emailTemplates: fullAccess,
    userPhotos: fullAccess,
    viewings: fullAccess,
    templates: fullAccess,
  },

  // HQ Admin - Full access to operational paths
  [UserRole.HQ_ADMIN]: {
    developments: fullAccess,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: fullAccess,
    documents: fullAccess,
    invoices: fullAccess,
    marketingAssets: fullAccess,
    emailTemplates: fullAccess,
    userPhotos: fullAccess,
    viewings: fullAccess,
    templates: fullAccess,
  },

  // HQ Bonds - Access to bond-related documents
  [UserRole.HQ_BONDS]: {
    developments: readOnly,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: readOnly,
    documents: fullAccess,
    invoices: readOnly,
    marketingAssets: readOnly,
    emailTemplates: noAccess,
    userPhotos: readOnly,
    viewings: readOnly,
    templates: fullAccess,
  },

  // HQ Consultant Level 1 - Limited write access
  [UserRole.HQ_CONSULTANT_LVL1]: {
    developments: readOnly,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: noAccess,
    documents: fullAccess,
    invoices: readOnly,
    marketingAssets: fullAccess,
    emailTemplates: noAccess,
    userPhotos: fullAccess, // Own photos only (enforced at UI level)
    viewings: fullAccess,
    templates: readOnly,
  },

  // HQ Independent Consultant Level 2 - Same as Level 1
  [UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2]: {
    developments: readOnly,
    deals: fullAccess,
    reservations: fullAccess,
    consultantLoans: noAccess,
    documents: fullAccess,
    invoices: readOnly,
    marketingAssets: fullAccess,
    emailTemplates: noAccess,
    userPhotos: fullAccess, // Own photos only (enforced at UI level)
    viewings: fullAccess,
    templates: readOnly,
  },

  // HQ Referral Mandate - Minimal access
  [UserRole.HQ_REFERRAL_MANDATE]: {
    developments: readOnly,
    deals: readOnly,
    reservations: readOnly,
    consultantLoans: noAccess,
    documents: readOnly,
    invoices: readOnly,
    marketingAssets: readOnly,
    emailTemplates: noAccess,
    userPhotos: fullAccess, // Own photos only
    viewings: noAccess,
    templates: readOnly,
  },

  // HQ Advertising - Marketing assets focus
  [UserRole.HQ_ADVERTISING]: {
    developments: readOnly,
    deals: noAccess,
    reservations: noAccess,
    consultantLoans: noAccess,
    documents: readOnly,
    invoices: noAccess,
    marketingAssets: fullAccess,
    emailTemplates: fullAccess,
    userPhotos: fullAccess, // Own photos only
    viewings: noAccess,
    templates: readOnly,
  },

  // HQ Viewing Agent - Viewing-related documents
  [UserRole.HQ_VIEWING_AGENT]: {
    developments: readOnly,
    deals: readOnly,
    reservations: readOnly,
    consultantLoans: noAccess,
    documents: readOnly,
    invoices: noAccess,
    marketingAssets: readOnly,
    emailTemplates: noAccess,
    userPhotos: fullAccess, // Own photos only
    viewings: fullAccess,
    templates: noAccess,
  },

  // HQ Viewing Coordinator - Full viewing oversight
  [UserRole.HQ_VIEWING_COORDINATOR]: {
    developments: readOnly,
    deals: readOnly,
    reservations: fullAccess,
    consultantLoans: noAccess,
    documents: fullAccess,
    invoices: noAccess,
    marketingAssets: readOnly,
    emailTemplates: noAccess,
    userPhotos: fullAccess,
    viewings: fullAccess,
    templates: noAccess,
  },

  // AI Agent (Level 1) — ARIA operates server-side (Admin SDK); the browser
  // storage surface is entirely closed for this role.
  [UserRole.AI_AGENT_LVL1]: {
    developments: noAccess,
    deals: noAccess,
    reservations: noAccess,
    consultantLoans: noAccess,
    documents: noAccess,
    invoices: noAccess,
    marketingAssets: noAccess,
    emailTemplates: noAccess,
    userPhotos: noAccess,
    viewings: noAccess,
    templates: noAccess,
  },
};

/**
 * Get default storage permissions for a role
 */
export const getDefaultStoragePermissions = (role: UserRole): StoragePermissions | null => {
  return DEFAULT_STORAGE_PERMISSIONS[role] || null;
};

// ============================================================
// Role Definitions with Descriptions
// ============================================================

export interface RoleDefinitionAudit {
  id: UserRole;
  name: string;
  description: string;
  commissionPercentage?: number;
  category: 'system' | 'management' | 'sales' | 'support';
  isActive: boolean;
}

export const ROLE_DEFINITIONS: RoleDefinitionAudit[] = [
  {
    id: UserRole.PROPCLOUDAI_SUPER_USER,
    name: 'Super Admin',
    description: 'Full system access with ability to manage all users and settings',
    category: 'system',
    isActive: true,
  },
  {
    id: UserRole.HQ_MANAGER,
    name: 'HQ Manager',
    description: 'HQ operations management with full access to sales and admin features',
    category: 'management',
    isActive: true,
  },
  {
    id: UserRole.HQ_ADMIN,
    name: 'HQ Admin',
    description: 'Administrative tasks and support for HQ operations',
    category: 'management',
    isActive: true,
  },
  {
    id: UserRole.HQ_BONDS,
    name: 'HQ Bonds',
    description: 'Bond application management and financial document processing',
    category: 'management',
    isActive: true,
  },
  {
    id: UserRole.HQ_CONSULTANT_LVL1,
    name: 'HQ Consultant (Level 1)',
    description: 'Junior consultant with 30% commission on sales',
    commissionPercentage: 30,
    category: 'sales',
    isActive: true,
  },
  {
    id: UserRole.HQ_INDEPENDENT_CONSULTANT_LVL2,
    name: 'HQ Independent Consultant (Level 2)',
    description: 'Senior consultant with 50% commission on sales',
    commissionPercentage: 50,
    category: 'sales',
    isActive: true,
  },
  {
    id: UserRole.HQ_REFERRAL_MANDATE,
    name: 'HQ Referral Mandate',
    description: 'Referral mandate earning 5% on referred sales',
    commissionPercentage: 5,
    category: 'sales',
    isActive: true,
  },
  {
    id: UserRole.HQ_ADVERTISING,
    name: 'HQ Advertising',
    description: 'Marketing and advertising team member',
    category: 'support',
    isActive: true,
  },
  {
    id: UserRole.HQ_VIEWING_AGENT,
    name: 'HQ Viewing Agent',
    description: 'Property viewing management and client tours',
    category: 'support',
    isActive: true,
  },
  {
    id: UserRole.HQ_VIEWING_COORDINATOR,
    name: 'HQ Viewing Coordinator',
    description: 'Complete oversight and coordination of all viewings',
    category: 'support',
    isActive: true,
  },
  {
    id: UserRole.HQ_CALL_CENTRE_AGENT,
    name: 'HQ Call Centre Agent',
    description: 'Call centre agent handling calls via Euphoria dialer',
    category: 'support',
    isActive: true,
  },
  {
    id: UserRole.AI_AGENT_LVL1,
    name: 'AI Agent (Level 1)',
    description:
      'AI sales consultant (server-side actor). Owns and works assigned leads via the ARIA tool layer; no interactive login, no commission, no deal-capture rights.',
    commissionPercentage: 0,
    category: 'support',
    isActive: true,
  },
];

// Helper function to get card by ID
export const getCardById = (cardId: string): DashboardCardDefinition | undefined => {
  return DASHBOARD_CARDS.find((card) => card.id === cardId);
};

// Helper function to get menu groups for a card
export const getMenuGroupsForCard = (cardId: string): MenuGroupDefinition[] => {
  if (cardId === 'hq') return HQ_MENU_GROUPS;
  if (cardId === 'crm') return CRM_MENU_GROUPS;
  if (cardId === 'advertising') return ADVERTISING_MENU_GROUPS;
  return [];
};

// Helper function to check if a role has default access to a card
export const roleHasDefaultCardAccess = (role: UserRole, cardId: string): boolean => {
  const card = getCardById(cardId);
  if (!card) return false;
  return card.defaultEnabledRoles.includes(role);
};

// Helper function to get storage permissions for a role
export const getStoragePermissionsForRole = (role: UserRole): StoragePermissionDefinition[] => {
  return STORAGE_PERMISSIONS.filter(
    (perm) => perm.readRoles.includes(role) || perm.writeRoles.includes(role)
  );
};
